import { Link, useParams } from 'react-router-dom';
import { PRIVACY_VERSION, TOS_VERSION } from '../../shared/legal.js';
import { AppShell } from '../components/AppShell.js';

const content = {
  privacy: {
    title: 'Privacy notice',
    version: PRIVACY_VERSION,
    sections: [
      [
        'Controller',
        'The controller is Refindi Sp. z o.o., ul. Pulawska 2 (building B), 02-566 Warsaw, Poland, email: info@refindi.com.',
      ],
      [
        'Why we process data',
        'We process data to receive and investigate a lost-item report, coordinate shipment or pickup, confirm return, communicate with you and the venue, protect the service, and measure its effectiveness. The service does not make automated decisions about matching or ownership.',
      ],
      [
        'Data involved',
        'We process the item description, date and place, optional proof description or photo, your name and contact details, a redacted conversation transcript, case history, return choice, optional delivery address and instructions, carrier tracking, notification delivery records, and privacy-safe service events. Refindi does not store audio or raw IP addresses. Do not provide a PIN, CVV, password, full card number, or unlock pattern.',
      ],
      [
        'Legal basis',
        'The report is handled to take steps at your request. Security, audit, and non-identifying service measurement rely on Refindi’s legitimate interests. Fields marked optional are not required to submit a report.',
      ],
      [
        'Recipients and providers',
        'Authorized Refindi administrators have full access. A venue employee with the private case link sees the report details needed to investigate and, after you provide them, the shipment or pickup instructions. The venue does not see your conversation transcript or general contact fields. Processors include Google Cloud, OpenAI, and Resend. OpenAI does not train its models on API data unless data sharing is explicitly enabled, but may retain abuse-monitoring logs for up to 30 days under the applicable configuration and contract.',
      ],
      [
        'Processing location',
        'The application, database, and private photos are hosted in the Warsaw region. OpenAI uses its global API endpoint, so its processing is not guaranteed to remain in the European Economic Area. Applicable provider agreements and transfer safeguards must be approved by Refindi.',
      ],
      [
        'Retention',
        'Reports, cases, return details, notification records, photos, and raw service events are deleted after 30 days. Administrator audit records are deleted after 90 days. Non-identifying daily aggregates are kept for up to 12 months.',
      ],
      [
        'Your rights',
        'You may request access, correction, deletion, restriction, portability, or object by emailing info@refindi.com. You may also complain to the Polish data protection authority. Some rights depend on the applicable legal basis and Refindi’s obligations.',
      ],
    ],
  },
  terms: {
    title: 'Terms of use',
    version: TOS_VERSION,
    sections: [
      [
        'Service scope',
        'The service lets you send one lost-item report through a venue invitation, track its review, choose shipment or pickup after a possible match, receive tracking or pickup instructions, and confirm return. A submitted report does not guarantee that the venue will find or match the item.',
      ],
      [
        'Your responsibilities',
        'Provide information to the best of your knowledge, report only an item you are entitled to claim, and do not submit payment data, PINs, passwords, CVVs, or unlock codes. By adding a photo, you confirm it may be used to handle the report.',
      ],
      [
        'Urgent and sensitive items',
        'The service does not replace direct contact with the venue, police, bank, document issuer, or device provider. Block a payment card and secure a device independently of this report. Refindi does not mediate cash transfers.',
      ],
      [
        'Contact and availability',
        'Refindi and the invited venue may contact you only about the report and return. Keep your private tracking link secure. The service may be changed, temporarily unavailable, or discontinued, and submission does not guarantee a response time.',
      ],
      [
        'Shipment and cost',
        'Submitting a report is free. The venue arranges any courier or postal delivery outside this application and records the resulting tracking details. Any delivery fee, insurance, customs obligation, identity check, or additional service must be agreed separately with the venue.',
      ],
      [
        'Contact',
        'Service provider: Refindi Sp. z o.o., ul. Pulawska 2 (building B), 02-566 Warsaw, Poland, KRS 0001028942, NIP 1133091953, email: info@refindi.com.',
      ],
    ],
  },
} as const;

export function LegalPage() {
  const { document } = useParams();
  const type = document === 'terms' ? 'terms' : 'privacy';
  const page = content[type];

  return (
    <AppShell language="en">
      <article className="legal-page">
        <header>
          <span className="legal-version">Version {page.version}</span>
          <h1>{page.title}</h1>
        </header>
        {page.sections.map(([title, body]) => (
          <section key={title}>
            <h2>{title}</h2>
            <p>{body}</p>
          </section>
        ))}
        <Link className="button button-secondary" to="/report/contact">
          Return to the report
        </Link>
      </article>
    </AppShell>
  );
}
